import { supabaseData } from '../../utils/supabase-data-client';
import { Category, Bookmark } from '../../types/bookmarks';
import { NoteCategory, Note, NoteTemplate } from '../../types/notes';

export type RefetchKey =
  | 'categories'
  | 'bookmarks'
  | 'note_categories'
  | 'notes'
  | 'note_templates';

interface CacheStoreSetter {
  setCategories: (categories: Category[]) => boolean;
  setBookmarks: (bookmarks: Bookmark[]) => boolean;
  setNoteCategories: (categories: NoteCategory[]) => boolean;
  setNotes: (notes: Note[]) => boolean;
  setNoteTemplates: (templates: NoteTemplate[]) => boolean;
}

export async function refetchAndCache(
  cacheKey: RefetchKey,
  cacheStore: CacheStoreSetter,
): Promise<void> {
  switch (cacheKey) {
    case 'categories': {
      const { data, error } = await supabaseData
        .from('bookmark_categories')
        .select('*')
        .order('category_name');
      if (error) {
        console.error('Failed to refetch categories:', error);
        return;
      }
      cacheStore.setCategories(data as Category[]);
      return;
    }
    case 'bookmarks': {
      const { data, error } = await supabaseData
        .from('bookmarks')
        .select('*')
        .order('bookmark_name');
      if (error) {
        console.error('Failed to refetch bookmarks:', error);
        return;
      }
      cacheStore.setBookmarks(data as Bookmark[]);
      return;
    }
    case 'note_categories': {
      const { data, error } = await supabaseData
        .from('note_categories')
        .select('*')
        .order('category_name');
      if (error) {
        console.error('Failed to refetch note categories:', error);
        return;
      }
      cacheStore.setNoteCategories(data as NoteCategory[]);
      return;
    }
    case 'notes': {
      // Active notes only — trashed notes (deleted_at not null) are fetched
      // on demand by the Trash view, never cached.
      const { data, error } = await supabaseData
        .from('notes')
        .select('*')
        .is('deleted_at', null)
        .order('note_title');
      if (error) {
        console.error('Failed to refetch notes:', error);
        return;
      }
      cacheStore.setNotes(data as Note[]);
      return;
    }
    case 'note_templates': {
      const { data, error } = await supabaseData
        .from('note_templates')
        .select('*')
        .order('template_name');
      if (error) {
        console.error('Failed to refetch note templates:', error);
        return;
      }
      cacheStore.setNoteTemplates(data as NoteTemplate[]);
      return;
    }
  }
}
